/**
 * ContentRefreshPreviewSlot
 *
 * Renders a live-polling refresh queue table for the content refresh agent
 * inside a chat behaviour slot. Each chat entry is started right away through
 * the same SOR endpoint as /ui/blogs' "Refresh existing blog"
 * (refresh_now_by_url), and the table shows the tenant's refreshed blogs with
 * their live status. The old key_val TODO queue relied on a scheduled
 * trigger that the SOR refresher no longer has, so those entries never ran.
 *
 * Action buttons open the refresh's review/result page, or /ui/blogs,
 * keeping the slot as a lightweight preview.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { Maximize2 } from 'lucide-react';
import { IconButton } from '@strivelabs/ui-core/v2';
import { LoadingSkeleton } from '@strivelabs/ui-blocks';
import {
  SlotCanvasLayout,
  SlotCanvasPreview,
  useRegisterSlotCanvas,
  useSlotCanvasContext,
} from '@/components/ai-elements/slot-canvas';
import { useSlotCanvasStore } from '@/store/slotCanvasStore';
import { EXPERIENCE_LABELS } from '@/components/ai-elements/slot-navigator/slot-navigator-utils';
import { z } from 'zod/v4';
import type { BehaviourAgentExperienceAction } from '@/types/ui-blocks';
import { ROUTES } from '@/agents/blog-post-content-refresher/constants';
import { ContentRefreshTable } from '@/components/content-refresh/ContentRefreshTable';
import { ProcessingStatus } from '@/hooks/useKeyValOperations';
import { useSorBlogQueue, type SorQueueSubmitResult } from '@/hooks/useSorBlogQueue';
import { useResolveWorkflowInstanceId } from '@/hooks/useResolveWorkflowInstanceId';
import { refreshNowByUrl } from '@/services/DataService';
import { toRefreshQueueRow } from '@/utils/sorBlogQueueRows';
import { validateUrl } from '@/lib/utils';
import { usePersistentEntryStore } from '@/hooks/usePersistentEntryStore';
import { useChatMetadata } from '@/contexts/ChatMetadataContext';
import {
  FullscreenSlotOverlay,
  SlotContainer,
  parseKeywords,
  getSlotActionButton,
} from './shared';

/**
 * Zod schema for Content Refresh entry validation. Every field is nullish:
 * the chat tool's own schema sends `null` for anything it has no value for,
 * which must not fail the whole entry.
 */
const ContentRefreshEntrySchema = z.object({
  blog_post_url: z.string().nullish(),
  primary_keywords: z.union([z.array(z.string()), z.string()]).nullish(),
  content_focus_angle: z.string().nullish(),
});

/** Where the old agent page lived; its blogs now live on /ui/blogs. */
const BLOGS_PATH = '/ui/blogs';

/**
 * Starts one chat entry's refresh through refresh_now_by_url, the same call
 * /ui/blogs' "Refresh existing blog" makes: it finds the blog by URL (or
 * starts tracking it) and refreshes it now.
 *
 * @param entry - The raw chat entry.
 *
 * @returns Whether the refresh was started.
 */
export async function startRefreshFromChatEntry (entry: unknown): Promise<SorQueueSubmitResult> {
  const parsed = ContentRefreshEntrySchema.safeParse(entry);
  if (!parsed.success) {
    console.warn('[ContentRefreshPreviewSlot] Invalid entry format:', parsed.error);
    return 'skipped';
  }
  const e = parsed.data;
  const url = e.blog_post_url?.trim() ?? '';
  if (!url || !validateUrl(url)) {
    return 'skipped';
  }
  const primary = parseKeywords(e.primary_keywords);
  const contentFocusAngle = e.content_focus_angle?.trim();

  const result = await refreshNowByUrl({
    url,
    ...(primary.length > 0 ? { primary_keywords: primary } : {}),
    ...(contentFocusAngle ? { content_focus_angle: contentFocusAngle } : {}),
  });

  return result.status === 'claimed' ? 'claimed' : 'rejected';
}

export interface ContentRefreshPreviewSlotProps {
  /** Stable slot id (behaviour.id) used for canvas registration + SlotNavigator */
  slotId?: string;
  action: BehaviourAgentExperienceAction;
  chatHistoryId?: string;
  behaviourActionState?: Record<string, unknown>;
}

export function ContentRefreshPreviewSlot({
  slotId,
  action,
  chatHistoryId,
  behaviourActionState,
}: ContentRefreshPreviewSlotProps) {
  const canvasCtx = useSlotCanvasContext();
  const isCanvasMode = canvasCtx.mode === 'canvas';
  const canvasBodyMount = useSlotCanvasStore((s) => s.bodyMountElement);
  const resolvedSlotId = slotId ?? action.id;
  const resolvedTitle = action.data?.title ?? EXPERIENCE_LABELS[action.experience];
  const canvasRegistration = useRegisterSlotCanvas({
    id: resolvedSlotId,
    experience: action.experience,
    title: resolvedTitle,
    chatHistoryId,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const [chatRoot, setChatRoot] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  const agentShortName = action.agent_short_name;
  const resolvedWorkflowInstanceId = useResolveWorkflowInstanceId(agentShortName);
  const workflowInstanceId = resolvedWorkflowInstanceId ?? '';
  const entries = action.data?.entries;

  const { workflowInstanceId: ctxWorkflowInstanceId, workflowExecutionId } = useChatMetadata();

  // Use hook to manage persistent entry storage state
  const { alreadyStored, handleStoreSuccess } = usePersistentEntryStore(
    action,
    behaviourActionState,
    chatHistoryId,
    ctxWorkflowInstanceId,
    workflowExecutionId
  );

  const { blogs, isStoring, storeError, isLoading, isError } = useSorBlogQueue({
    source: 'refresh',
    entries,
    actionId: action.id,
    alreadyStored: alreadyStored || Boolean(action.from_history),
    onStoreSuccess: handleStoreSuccess,
    submitEntry: startRefreshFromChatEntry,
    enabled: !!workflowInstanceId,
  });
  const data = useMemo(() => blogs.map(toRefreshQueueRow), [blogs]);

  const handleNavigateToAgent = useCallback(() => {
    navigate(BLOGS_PATH);
  }, [navigate]);

  const renderAction = useCallback(
    (row: { _status: ProcessingStatus; data?: { workflow_execution_id?: string }; key?: string }) => {
      // For completed rows, navigate directly to the result page
      if (row._status === ProcessingStatus.COMPLETED && row.data?.workflow_execution_id && row.key) {
        const resultUrl = `${ROUTES.result(row.data.workflow_execution_id)}?key=${encodeURIComponent(row.key)}&from=blogs`;
        return getSlotActionButton({
          status: row._status,
          onNavigateToAgent: () => navigate(resultUrl),
        });
      }
      // For waiting-for-approval rows, navigate directly to the review page
      if (row._status === ProcessingStatus.WAITING_FOR_APPROVAL && row.data?.workflow_execution_id && row.key) {
        const reviewUrl = `${ROUTES.review(row.data.workflow_execution_id)}?key=${encodeURIComponent(row.key)}&from=blogs`;
        return getSlotActionButton({
          status: row._status,
          onNavigateToAgent: () => navigate(reviewUrl),
        });
      }
      return getSlotActionButton({
        status: row._status,
        onNavigateToAgent: handleNavigateToAgent,
      });
    },
    [handleNavigateToAgent, navigate]
  );

  // Resolve the chat container on mount
  useEffect(() => {
    if (slotRef.current) {
      const root = slotRef.current.closest<HTMLElement>('[data-chat-root]');
      if (root) setChatRoot(root);
    }
  }, []);

  if (!workflowInstanceId) return null;

  // -- Render helpers ---------------------------------------------------------

  const renderBodyContent = () => {
    if (isStoring) {
      return (
        <div className="p-10">
          <LoadingSkeleton />
          <p className="text-sm text-muted-foreground mt-2">
            Starting refreshes...
          </p>
        </div>
      );
    }
    if (isLoading) {
      return (
        <div className="p-10">
          <LoadingSkeleton />
        </div>
      );
    }
    if (isError || data.length === 0) {
      return (
        <div className="p-10">
          <p className="text-sm text-muted-foreground">
            {isError
              ? 'Unable to load content refresh tasks.'
              : 'No refreshes yet.'}
          </p>
        </div>
      );
    }
    return (
      <>
        {storeError && (
          <div className="mx-6 mt-6 mb-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">
              Some refreshes could not be started. Showing existing refreshes.
            </p>
          </div>
        )}
        <div className={storeError ? 'px-6 pb-6' : 'p-8'}>
          <ContentRefreshTable data={data} renderAction={renderAction} />
        </div>
      </>
    );
  };

  // -- Canvas mode ------------------------------------------------------------
  if (isCanvasMode) {
    return (
      <>
        <SlotCanvasPreview
          id={resolvedSlotId}
          experience={action.experience}
          title={resolvedTitle}
          isActive={canvasRegistration.isOpen}
          onClick={canvasRegistration.open}
        />
        {canvasRegistration.isOpen &&
          canvasBodyMount &&
          createPortal(
            <SlotCanvasLayout
              title={resolvedTitle}
              subtitle={EXPERIENCE_LABELS[action.experience]}
            >
              {renderBodyContent()}
            </SlotCanvasLayout>,
            canvasBodyMount,
          )}
      </>
    );
  }

  // -- Inline mode (legacy path, unchanged) -----------------------------------

  // Store phase loading state
  if (isStoring) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <LoadingSkeleton />
          <p className="text-sm text-muted-foreground mt-2">
            Starting refreshes...
          </p>
        </div>
      </SlotContainer>
    );
  }

  // Fetch phase loading state
  if (isLoading) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <LoadingSkeleton />
        </div>
      </SlotContainer>
    );
  }

  // Error or empty state
  if (isError || data.length === 0) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <p className="text-sm text-muted-foreground">
            {isError ? 'Unable to load content refresh tasks.' : 'No refreshes yet.'}
          </p>
        </div>
      </SlotContainer>
    );
  }

  return (
    <>
      <SlotContainer slotRef={slotRef}>
        {/* Expand button - positioned at top right */}
        <IconButton variant="secondary" size="2xs"
          onClick={() => setIsExpanded(true)}
          className="absolute top-2.5 right-2.5 z-10"
          aria-label="Expand to fullscreen"
        >
          <Maximize2 className="size-3.5 text-foreground" />
        </IconButton>

        {/* Store error warning */}
        {storeError && (
          <div className="mx-6 mt-6 mb-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">
              Some refreshes could not be started. Showing existing refreshes.
            </p>
          </div>
        )}

        {/* Table content */}
        <div className={storeError ? "px-6 pb-6" : "p-8"}>
          <ContentRefreshTable data={data} renderAction={renderAction} />
        </div>
      </SlotContainer>

      {/* Fullscreen overlay — portalled into the chat container */}
      <AnimatePresence>
        {isExpanded && chatRoot && (
          <FullscreenSlotOverlay
            onClose={() => setIsExpanded(false)}
            portalTarget={chatRoot}
          >
            <ContentRefreshTable data={data} renderAction={renderAction} />
          </FullscreenSlotOverlay>
        )}
      </AnimatePresence>
    </>
  );
}
